import { z as register } from "./vendors.js";
import { r as reanimateImages } from "./utilities-utils.js";
import "./swiper-modules.js";
import "./utilities-nav-utils.js";
register();
class SwiperInit {
  constructor() {
    this.initializedSwipers = /* @__PURE__ */ new WeakSet();
    this.observer = null;
    this.initializeSwipers();
    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        this.initializeSwipers();
      });
      document.addEventListener("shopify:section:unload", (event) => {
        this.cleanupSection(event);
      });
    }
  }
  cleanupSection(event) {
    const sectionId = event?.detail?.sectionId;
    if (!sectionId) return;
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;
    const swipers = section.querySelectorAll("swiper-container");
    swipers.forEach((swiperEl) => {
      if (swiperEl._resizeHandler) {
        window.removeEventListener("resize", swiperEl._resizeHandler);
        swiperEl._resizeHandler = null;
      }
      if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
      }
    });
  }
  initializeSwipers() {
    const swipers = document.querySelectorAll("swiper-container");
    if (Shopify.designMode) {
      swipers.forEach((swiperEl) => this.initSwiperElement(swiperEl));
      return;
    }
    if (!this.observer && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.initSwiperElement(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: "200px",
        // Start loading 200px before entering viewport
        threshold: 0.01
      });
    }
    swipers.forEach((swiperEl) => {
      if (!this.initializedSwipers.has(swiperEl)) {
        if (Shopify.designMode || this.observer && window.getComputedStyle(swiperEl).display !== "none") {
          this.observer?.observe(swiperEl);
        }
      }
    });
  }
  initSwiperElement(swiperEl) {
    if (this.initializedSwipers.has(swiperEl)) return;
    this.initializedSwipers.add(swiperEl);
    const mobileSlidesPerView = swiperEl.dataset.mobileSlidesPerView || "auto";
    swiperEl.dataset.desktopSlidesPerView || "auto";
    swiperEl.dataset.tabletSlidesPerView === "0" || !swiperEl.dataset.tabletSlidesPerView ? mobileSlidesPerView : swiperEl.dataset.tabletSlidesPerView;
    const hasTextPagination = swiperEl.dataset.textPagination === "true";
    const spaceBetweenMobile = Number(swiperEl.dataset.spaceBetweenMobile) || 1;
    const spaceBetweenTablet = Number(swiperEl.dataset.spaceBetweenTablet) || 1;
    const spaceBetweenDesktop = Number(swiperEl.dataset.spaceBetweenDesktop) || 1;
    const mobileLayout = swiperEl.dataset.mobileLayout || "slider";
    const desktopLayout = swiperEl.dataset.desktopLayout || "slider";
    const mobile = window.matchMedia("(min-width: 0px) and (max-width: 1023px)");
    const desktop = window.matchMedia("(min-width: 1024px)");
    const handleVideoPlayback = () => {
      const videos = swiperEl.querySelectorAll("video");
      videos.forEach((video) => {
        if (!video.classList.contains("section__wrapper-background")) {
          video.pause();
        }
      });
      const allSlides = Array.from(swiperEl.querySelectorAll("swiper-slide"));
      const visibleSlides = allSlides.filter(
        (slide) => slide.classList.contains("swiper-slide-visible")
      );
      const nonActiveSlides = allSlides.filter(
        (slide) => !slide.classList.contains("swiper-slide-active")
      );
      nonActiveSlides.forEach((slide) => {
        const type = (slide.getAttribute("data-type") || "").toLowerCase();
        const iframe = slide.querySelector("iframe");
        if (!iframe) return;
        if (iframe.tagName.toLowerCase() === "video") {
          iframe.pause();
        } else {
          var src = iframe.src;
          iframe.src = src;
        }
        try {
          if (type === "youtube") {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
          } else if (type === "vimeo") {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        } catch (e) {
        }
      });
      visibleSlides.forEach((slide) => {
        const video = slide.querySelector("video");
        if (video && video.hasAttribute("autoplay")) {
          video.play();
        }
      });
    };
    const updateTextPagination = () => {
      const parentContainer = swiperEl.closest(".slideshow-text-controls__wrapper");
      if (!parentContainer) return;
      const activeSlide = swiperEl.swiper.slides[swiperEl.swiper.realIndex];
      if (!activeSlide) return;
      const activeSlideId = activeSlide.getAttribute("data-slide");
      const textItems = parentContainer.querySelectorAll(".slideshow-text-controls__item");
      textItems.forEach((item) => {
        item.classList.toggle("active", item.getAttribute("data-slide") === activeSlideId);
      });
    };
    const initThumbnails = () => {
      const thumbnailsContainer = swiperEl.closest(".main-product__media-wrapper")?.querySelector(".swiper-thumbnails .swiper");
      if (!thumbnailsContainer) return;
      const thumbnailsParams = {
        direction: "vertical",
        slidesPerView: "auto",
        spaceBetween: 8,
        watchSlidesProgress: true,
        navigation: {
          nextEl: ".swiper-button-thumbs-next",
          prevEl: ".swiper-button-thumbs-prev"
        },
        breakpoints: {
          0: {
            direction: "horizontal",
            slidesPerView: 4
          },
          768: {
            direction: "vertical",
            slidesPerView: "auto"
          }
        }
      };
      Object.assign(thumbnailsContainer, thumbnailsParams);
      thumbnailsContainer.initialize();
    };
    const setupTextPaginationControls = () => {
      if (!hasTextPagination) return;
      const parentContainer = swiperEl.closest(".slideshow-text-controls__wrapper");
      if (!parentContainer) return;
      const textItems = parentContainer.querySelectorAll(".slideshow-text-controls__item");
      textItems.forEach((item) => {
        item.addEventListener("click", () => {
          const slideId = item.getAttribute("data-slide");
          const slideIndex = Array.from(swiperEl.swiper.slides).findIndex(
            (slide) => slide.getAttribute("data-slide") === slideId
          );
          if (slideIndex !== -1) {
            swiperEl.swiper.slideTo(slideIndex);
          }
        });
      });
    };
    const buildSlider = () => {
      const shouldBeSlider = desktop.matches && desktopLayout === "slider" || mobile.matches && mobileLayout === "slider";
      if (shouldBeSlider) {
        if (!swiperEl.swiper || swiperEl.swiper.destroyed) {
          const initialMobileSlidesPerView = parseFloat(swiperEl.dataset.mobileSlidesPerView) || "auto";
          const initialTabletSlidesPerView = swiperEl.dataset.tabletSlidesPerView === "0" || !swiperEl.dataset.tabletSlidesPerView ? initialMobileSlidesPerView : parseFloat(swiperEl.dataset.tabletSlidesPerView);
          const initialDesktopSlidesPerView = parseFloat(swiperEl.dataset.desktopSlidesPerView) || "auto";
          const params = {
            ...swiperEl.closest(".card__image") ? {
              virtual: {
                enabled: true
              },
              nested: true
            } : {},
            breakpoints: {
              0: {
                slidesPerView: initialMobileSlidesPerView,
                spaceBetween: parseInt(spaceBetweenMobile)
              },
              768: {
                slidesPerView: initialTabletSlidesPerView,
                spaceBetween: parseInt(spaceBetweenTablet)
              },
              1024: {
                slidesPerView: initialDesktopSlidesPerView,
                spaceBetween: parseInt(spaceBetweenDesktop)
              }
            }
          };
          Object.assign(swiperEl, params);
          swiperEl.addEventListener("swiperafterinit", () => {
            const parentSection = swiperEl.closest(".shopify-section") || swiperEl.closest(".main-product__media");
            if (parentSection) {
              parentSection.classList.add("slider-enabled");
            }
            setTimeout(() => {
              if (!swiperEl.swiper) {
                return;
              }
              const totalSlides = swiperEl.swiper?.slides?.length || 0;
              const adjustedMobileSlidesPerView = totalSlides < initialMobileSlidesPerView ? totalSlides : initialMobileSlidesPerView;
              const adjustedTabletSlidesPerView = totalSlides < initialTabletSlidesPerView ? totalSlides : initialTabletSlidesPerView;
              const adjustedDesktopSlidesPerView = totalSlides < initialDesktopSlidesPerView ? totalSlides : initialDesktopSlidesPerView;
              if (swiperEl.swiper && swiperEl.swiper.params && swiperEl.swiper.params.breakpoints) {
                swiperEl.swiper.currentBreakpoint = false;
                swiperEl.swiper.params.breakpoints[0].slidesPerView = adjustedMobileSlidesPerView;
                swiperEl.swiper.params.breakpoints[768].slidesPerView = adjustedTabletSlidesPerView;
                swiperEl.swiper.params.breakpoints[1024].slidesPerView = adjustedDesktopSlidesPerView;
                swiperEl.swiper.update();
                swiperEl.swiper.slideTo(0, 0, false);
              }
              handleVideoPlayback();
              initThumbnails();
              reanimateImages(swiperEl);
            }, 0);
          });
          swiperEl.addEventListener("swiperslidechange", () => {
            setTimeout(() => {
              handleVideoPlayback();
              updateTextPagination();
              reanimateImages(swiperEl);
            }, 0);
          });
          swiperEl.addEventListener("swiperdestroy", () => {
            const parentSection = swiperEl.closest(".shopify-section");
            if (parentSection) {
              parentSection.classList.remove("slider-enabled");
            }
            if (swiperEl._resizeHandler) {
              window.removeEventListener("resize", swiperEl._resizeHandler);
              swiperEl._resizeHandler = null;
            }
          });
          swiperEl.addEventListener("swiperlock", () => {
            const parentSection = swiperEl.closest(".shopify-section");
            if (parentSection && !parentSection.classList.contains("recommended-products")) {
              setTimeout(() => {
                const sliderNavs = parentSection.querySelectorAll(".grid-content__slider-nav");
                sliderNavs.forEach((nav) => {
                  nav.style.display = "none";
                });
              }, 1);
            }
          });
          swiperEl.initialize();
          if (hasTextPagination) {
            setupTextPaginationControls();
            updateTextPagination();
          }
        } else {
          swiperEl.swiper.update();
        }
      } else {
        Object.assign(swiperEl);
        swiperEl.initialize();
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true);
          const parentSection = swiperEl.closest(".shopify-section");
          if (parentSection) {
            parentSection.classList.remove("slider-enabled");
          }
        }
      }
    };
    swiperEl._resizeHandler = buildSlider;
    window.addEventListener("resize", swiperEl._resizeHandler);
    buildSlider();
  }
}
const swiperInit = new SwiperInit();
const reinitializeSwipers = () => {
  swiperInit.initializeSwipers();
};
export {
  reinitializeSwipers as r
};
